import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import '../../styles/common.css';
import { Spinner, Container, Card, Button, Table, Form } from 'react-bootstrap';
import { calculateHourlyAverages, formatDateWithdateAndTime } from '../../Common/FilteredDateFormat';
import { Line } from 'react-chartjs-2'; // Import Line chart
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AirIcon from '@mui/icons-material/Air';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import OpacityIcon from '@mui/icons-material/Opacity';
import GrainIcon from '@mui/icons-material/Grain';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WarningModal from '../../Common/WarningModal';


ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);


function ZoneBasedSystem() {

  // Get the zone from the URL query parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const zoneValue = queryParams.get('deviceId');
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const navigate = useNavigate();

  // Get the current date and the date 6 months ago
  const currentDateForDateRange = new Date();
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(currentDateForDateRange.getMonth() - 6);
  const minDate = sixMonthsAgo.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  const maxDate = currentDateForDateRange.toISOString().split('T')[0]; // Format: YYYY-MM-DD


  // Mapping of zones to two device IDs
  const zoneDeviceMap = {
    zone1: ['SMAS0005', 'SMAS0006'],
    zone2: ['SMAS0007', 'SMAS0008'],
    zone3: ['SMAS0009', 'SMAS0010'],
    zone4: ['SMAS0011', 'SMAS0012'],
  }

  const [filterCurrentDateData, setFilterCurrentDateData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState('');
  const [isDataFiltered, setIsDataFiltered] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showChart, setShowChart] = useState(false); // State for chart visibility
  const [device1Data, setDevice1Data] = useState(null);
  const [device2Data, setDevice2Data] = useState(null);
  const [combinedData, setCombinedData] = useState(null);
  const [waringModal, setWaringModal] = useState(false);

  const handleFromDateClick = () => {
    fromDateRef.current.showPicker();
  };

  const handleToDateClick = () => {
    toDateRef.current.showPicker();
  };


  const currentDate = new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD

  useEffect(() => {
    if (zoneValue === null || zoneValue === '') {
      setWaringModal(true);
    }
  }, [zoneValue]);

  const handleWarningModalClose = () => {
    setWaringModal(false);
    navigate('/'); // Navigate to Home
  };

  //get data from APis by on 2 deviceIds based on zone
  useEffect(() => {
    const zoneKey = zoneValue?.toLowerCase();

    if (!zoneKey || !zoneDeviceMap[zoneKey]) return;

    const [id1, id2] = zoneDeviceMap[zoneKey];
    setLoading(true);
    setNoDataMessage('');
    setDevice1Data(null);
    setDevice2Data(null);

    const fetchDevice1 = fetch(`/api/moistureSensor/getDataByDeviceId?deviceId=${id1}`).then(res =>
      res.json()
    );
    const fetchDevice2 = fetch(`/api/moistureSensor/getDataByDeviceId?deviceId=${id2}`).then(res =>
      res.json()
    );

    Promise.all([fetchDevice1, fetchDevice2])
      .then(([data1, data2]) => {
        setLoading(false);

        if (data1 && data1.length > 0) {
          setDevice1Data(data1);
          // filterDataByCurrentDate(data1, id1);
        } else {
          console.warn(`No data for ${id1}`);
        }

        if (data2 && data2.length > 0) {
          setDevice2Data(data2);
          // filterDataByCurrentDate(data2, id2);
        } else {
          console.warn(`No data for ${id2}`);
        }

        if ((!data1 || data1.length === 0) && (!data2 || data2.length === 0)) {
          setNoDataMessage('No data available for the selected zone.');
        }

        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching zone data:', error);
        setLoading(false);
        setNoDataMessage('Error fetching data for selected zone.');
      });
  }, [zoneValue]);

  //combine data both DeviceIds  
  useEffect(() => {
    if (device1Data && device1Data.length > 0 && device2Data && device2Data.length > 0) {
      const dev1 = device1Data[0];
      const dev2 = device2Data[0];

      const combined = {
        p1: dev1.p1,
        p2: dev1.p2,
        p3: dev1.p3,
        p4: dev1.p4,
        p5: dev1.p5,
        p6: dev1.p6,
        p7: dev1.p7,
        p8: dev1.p8,
        p9: dev2.p1,
        p10: dev2.p2,
        p11: dev2.p3,
        p12: dev2.p4,
        p13: dev2.p5,
        p14: dev2.p6,
        p15: dev2.p7,
        p16: dev2.p8,
        createdAt: dev1.createdAt,
      };

      setCombinedData(combined);

      // Show current date data by default
      const today = currentDate;
      const filtered1 = device1Data.filter(d => d.createdAt.startsWith(today));
      const filtered2 = device2Data.filter(d => d.createdAt.startsWith(today));
      const combinedArr = getCombinedLatestRecords(filtered1, filtered2, 10);
      setFilterCurrentDateData(combinedArr);
      setIsDataFiltered(false);
      setShowChart(false); // Hide chart by default
    }
  }, [device1Data, device2Data]);


  // Function to get the latest records from both devices and combine them
  function getCombinedLatestRecords(device1Data, device2Data, N = 10) {
    if (!device1Data || !device2Data) return [];
    const minLen = Math.min(device1Data.length, device2Data.length, N);
    const combinedArr = [];
    for (let i = 0; i < minLen; i++) {
      const dev1 = device1Data[i];
      const dev2 = device2Data[i];
      combinedArr.push({
        p1: dev1.p1,
        p2: dev1.p2,
        p3: dev1.p3,
        p4: dev1.p4,
        p5: dev1.p5,
        p6: dev1.p6,
        p7: dev1.p7,
        p8: dev1.p8,
        p9: dev2.p1,
        p10: dev2.p2,
        p11: dev2.p3,
        p12: dev2.p4,
        p13: dev2.p5,
        p14: dev2.p6,
        p15: dev2.p7,
        p16: dev2.p8,
        createdAt: dev1.createdAt,
      });
    }
    return combinedArr;
  }

  // function to calculate hourly averages
  const filterDataByCurrentDateData = (data, date) => {
    const filtered = data.filter((item) => item.createdAt.startsWith(date));
    const hourlyAverages = calculateHourlyAverages(filtered);
    setFilterCurrentDateData(hourlyAverages);
  };

  // Function to show all data
  const showAllData = () => {
    if (device1Data && device2Data) {
      const combinedArr = getCombinedLatestRecords(device1Data, device2Data, 10);
      setFilterCurrentDateData(combinedArr);
      setIsDataFiltered(false);
      setShowChart(false); // Hide chart
    }
  };

  // Function to show current date data
  const showCurrentDateData = () => {
    if (device1Data && device2Data) {
      const today = currentDate;
      const filtered1 = device1Data.filter(d => d.createdAt.startsWith(today));
      const filtered2 = device2Data.filter(d => d.createdAt.startsWith(today));
      const combinedArr = getCombinedLatestRecords(filtered1, filtered2, 10);
      setFilterCurrentDateData(combinedArr);
    } else {
      setFilterCurrentDateData([]);
    }
    setIsDataFiltered(false);
    setShowChart(false); // Hide chart
  };

  // Function to fetch data by date range for the selected zone
  async function fetchZoneDataByDateRange(zoneKey, fromDate, toDate) {
    if (!zoneKey || !zoneDeviceMap[zoneKey]) return [[], []];
    const [id1, id2] = zoneDeviceMap[zoneKey];

    const url1 = `/api/moistureSensor/getDataByDeviceIdWithDateRange?deviceId=${id1}&fromDate=${fromDate}&toDate=${toDate}`;
    const url2 = `/api/moistureSensor/getDataByDeviceIdWithDateRange?deviceId=${id2}&fromDate=${fromDate}&toDate=${toDate}`;

    const [data1, data2] = await Promise.all([
      fetch(url1).then(res => res.json()),
      fetch(url2).then(res => res.json())
    ]);
    return [data1, data2];
  }

  // Function to handle date range filter data
  const handleDateRangeFilterData = async () => {
    if (zoneValue && fromDate && toDate) {
      setLoading(true);
      setNoDataMessage('');
      const zoneKey = zoneValue.toLowerCase();
      try {
        const [data1, data2] = await fetchZoneDataByDateRange(zoneKey, fromDate, toDate);

        if ((!data1 || data1.length === 0) && (!data2 || data2.length === 0)) {
          setNoDataMessage('No data available for the selected date range.');
          setFilterCurrentDateData([]);
          setShowChart(false);
          setLoading(false);
          return;
        }

        // Combine the data for the table
        const combinedArr = getCombinedLatestRecords(data1, data2, 1000); // 1000 = all, or set as needed
        setFilterCurrentDateData(combinedArr);
        setIsDataFiltered(combinedArr.length > 0);
        setShowChart(true);
      } catch (error) {
        setNoDataMessage('Error fetching data for selected date range.');
        setFilterCurrentDateData([]);
        setShowChart(false);
        console.error(error);
      }
      setLoading(false);
    } else {
      setNoDataMessage("Please select both from and to dates.");
    }
  };

  // Function to clear date range filter data
  const handleClearDateRangeFilterData = () => {
    setLoading(true);
    setTimeout(() => {
      setFromDate('');
      setToDate('');
      showCurrentDateData();
      setIsDataFiltered(false);
      setShowChart(false);
      setLoading(false);
    }, 300);
  };

  // Function to download filtered data as CSV
  const downloadCSV = () => {
    if (!filterCurrentDateData || filterCurrentDateData.length === 0) return;

    // Header row with column names mapped to their display names
    const header = columnsToDisplay.map(key => columnNames[key] || key).join(',');

    // Data rows
    const rows = filterCurrentDateData.map(row =>
      columnsToDisplay
        .map(key =>
          key === 'createdAt'
            ? `"${formatDateWithdateAndTime(row[key])}"`
            : `"${row[key]}"`
        )
        .join(',')
    );

    const csvContent = [header, ...rows].join('\n');

    // Encode the content using UTF-8 BOM for proper Excel display
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', `Filtered_Data_${zoneValue}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Column names mapping table columns to their display names
  const columnNames = {
    p1: "Soil Moisture 1 (%)",
    p2: "Soil Moisture 2 (%)",
    p3: "Soil Moisture 3 (%)",
    p4: "Soil Moisture 4 (%)",
    p5: "Soil Moisture 5 (%)",
    p6: "Soil Moisture 6 (%)",
    p7: "Soil Moisture 7 (%)",
    p8: "Soil Moisture 8 (%)",
    p9: "Soil Moisture 9 (%)",
    p10: "Soil Moisture 10 (%)",
    p11: "Soil Moisture 11 (%)",
    p12: "Soil Moisture 12 (%)",
    p13: "Soil Moisture 13 (%)",
    p14: "Soil Moisture 14 (%)",
    p15: "Soil Moisture 15 (%)",
    p16: "Soil Moisture 16 (%)",
    createdAt: "Timestamp"
  };

  const columnsToDisplay = Object.keys(filterCurrentDateData[0] || {}).filter(
    key => key !== 'id' && key !== 'deviceId'
  );

  return (
    <Container className="mt-4">
      <Card className="mx-auto mb-3">
        <Card.Header>
          <h2>Sensor Data for Device {zoneValue}</h2>
        </Card.Header>
        <Card.Body>

          {/* Latest Data card */}
          {combinedData && (
            <Card className="mb-4">
              <Card.Body>
                <h4>Last Updated Record (Date: {formatDateWithdateAndTime(combinedData.createdAt)})</h4>

                {/* Container for Temperature readings */}
                <Grid container spacing={2} mt={2} mb={2} >
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                     <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Ammonia icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 1(%):</strong> {combinedData.p1}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Nitrogen Dioxide icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 2(%):</strong> {combinedData.p2}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Sulfur Dioxide icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 3(%):</strong> {combinedData.p3}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                     <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Carbon Dioxide icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 4(%):</strong> {combinedData.p4}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Container for Soil Moisture readings */}
                <Grid container spacing={2} mb={2} >
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Oxygen icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 5(%):</strong> {combinedData.p5}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                     <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Ethanol icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 6(%):</strong> {combinedData.p6}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* pH icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 7:</strong> {combinedData.p7}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Silage Temperature icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 8(%):</strong> {combinedData.p8}
                      </Typography>
                    </Box>

                  </Grid>
                </Grid>

                {/* Container for additional readings */}
                <Grid container spacing={2} mb={2} >
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                     <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* TVOC icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 9(%):</strong> {combinedData.p9}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Silage Moisture icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 10(%):</strong> {combinedData.p10}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Silage EC icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 11(%):</strong> {combinedData.p11}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Silage EC icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 12(%):</strong> {combinedData.p12}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Silage EC icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 13(%):</strong> {combinedData.p13}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Silage EC icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 14(%):</strong> {combinedData.p14}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Silage EC icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 15(%):</strong> {combinedData.p15}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Box display="flex" alignItems="center">
                      <BlurOnIcon color="primary" sx={{ mr: 1 }} /> {/* Silage EC icon */}
                      <Typography variant="body1">
                        <strong>Soil Moisture 16(%):</strong> {combinedData.p16}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card.Body>
            </Card>
          )}


          {/* Date Filter Inputs */}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>From Date:</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                ref={fromDateRef}
                onChange={(e) => setFromDate(e.target.value)}
                onClick={handleFromDateClick}
                min={minDate}
                max={maxDate}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>To Date:</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                ref={toDateRef}
                onChange={(e) => setToDate(e.target.value)}
                onClick={handleToDateClick}
                min={minDate}
                max={maxDate}
              />
            </Form.Group>
            <Button variant="primary" onClick={handleDateRangeFilterData} className="me-2">
              Filter Data
            </Button>
            <Button variant="secondary" onClick={handleClearDateRangeFilterData}>
              Clear Filter
            </Button>
            <Button
              variant="success"
              onClick={downloadCSV}
              disabled={!isDataFiltered}
              className="ms-2"
            >
              Download CSV
            </Button>
          </Form>
        </Card.Body>
      </Card>




      {/* Show Loader when Data is loading */}
      {loading && (
        <div className="centered-spinner">
          <Spinner animation="border" className='large-spinner-size' />
          <span>Loading data...</span>
        </div>
      )}
      {/* Show when current date data is empty as we are showing by default current date data */}
      {!loading && (
        <>
          <div className="mb-3 mt-3">
            {/* Buttons for showing all data or current date data */}
            <Button variant="primary" className="me-2" onClick={showCurrentDateData}>
              Show Current Date Data
            </Button>
            <Button variant="secondary" onClick={showAllData}>
              Show All Data
            </Button>
          </div>
          {/* Display filtered data */}
          {filterCurrentDateData.length > 0 ? (
            <Card className="mt-4">
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      {columnsToDisplay.map((key, index) => (
                        <th key={index}>{columnNames[key] || key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filterCurrentDateData.map((row, index) => (
                      <tr key={index}>
                        {columnsToDisplay.map((key, idx) => (
                          <td key={idx}>
                            {key === 'createdAt'
                              ? new Date(row[key]).toLocaleString()
                              : (row[key] !== undefined && row[key] !== null && row[key] !== '' ? row[key] : '-')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ) : noDataMessage ? (
            <div className="alert alert-warning" role="alert"> // Display API's no data message
              {noDataMessage}
            </div>
          ) : (

            <div className="alert alert-warning" role="alert">
              No data available for the selected zone on the current day.

            </div>
            // Default message
          )}
        </>
      )}


      {/* show graphs : when user filter data by date then only graphs will display */}

      {showChart && filterCurrentDateData.length > 0 && (
        <Card className="mt-4">
          <Card.Header>
            <h5>Soil Moisture Graphs</h5>
          </Card.Header>
          <Card.Body>
            <Grid container spacing={2}>
              {Array.from({ length: 16 }, (_, i) => {
                const key = `p${i + 1}`;
                return (
                  <Grid item xs={12} sm={6} md={3} key={key}>
                    <Card className="mb-3">
                      <Card.Header style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        {columnNames[key]}
                      </Card.Header>
                      <Card.Body>
                        <Line
                          data={{
                            labels: filterCurrentDateData.map(item =>
                              new Date(item.createdAt).toLocaleString()
                            ),
                            datasets: [
                              {
                                label: columnNames[key],
                                data: filterCurrentDateData.map(item => parseFloat(item[key])),
                                borderColor: `hsl(${i * 22}, 70%, 50%)`,
                                borderWidth: 2,
                                fill: false,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            plugins: {
                              legend: { display: false },
                              title: { display: false },
                            },
                            scales: {
                              x: { title: { display: true, text: 'Timestamp' } },
                              y: { title: { display: true, text: columnNames[key] } },
                            },
                          }}
                        />
                      </Card.Body>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Card.Body>
        </Card>
      )}

      {/* Warning Modal */}
      <WarningModal
        show={waringModal}
        onClose={handleWarningModalClose}
        message="Please select a valid zone to view sensor data."
        title="Zone Not Selected"
      />
    </Container>
  );

}

export default ZoneBasedSystem;
